import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@bb-ui/react-library';
import {
  OnSortChangedParams,
  SortDirection,
} from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import {
  TableRow,
  SortableTableHeaderCell,
  TableCell,
  TableHead,
  IconButton,
  Typography,
} from '@bb-ui/react-library/dist/components';
import { StandardListPage } from 'components/StandardListPage';
import { PaginatedTable } from 'components/PaginatedTable';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { FleetData } from 'App.types';
import { Link } from 'react-router-dom';
import { CreateFleetButton } from './CreateFleetButton';
import { ChevronRight } from '@bb-ui/icons/dist/small';

// Loosely typing this, so we can use <ButtonBase> props.
const LooseIconButton = IconButton as any;

const useStyles = makeStyles(() => ({
  button: {
    minWidth: 'auto',
    height: '1.5rem',
    padding: '0.2rem 0.5rem',
    fontSize: '0.75rem',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.3rem',
    justifyContent: 'center',
  },
  enabledCell: {
    width: 20,
  },
}));

export const Fleets: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { data, error, loading, fetch: fleetFetch } = useRestApi(apiUrl('tenancy', 'fleets'));

  const handleReload = () => {
    fleetFetch();
  };

  const fleets = React.useMemo<FleetData[]>(
    () =>
      (data?.items ?? []).filter(
        (fleet: FleetData) => !fleet.name.startsWith('Integration Test Fleet:'),
      ),
    [data],
  );

  const search =
    (filter: string) =>
    ({ fleetId, region }: FleetData) =>
      fleetId.toLowerCase().includes(filter.toLowerCase()) ||
      region.toLowerCase().includes(filter.toLowerCase());

  const [sortParams, setSortParams] = React.useState<Partial<OnSortChangedParams>>({});

  const sortedFleets = React.useMemo(() => {
    const { sortColumnId, sortDirection } = sortParams;
    const orderSign = sortDirection === 'asc' ? 1 : -1;

    const getComparator = (sortColumnId: string | undefined) => {
      switch (sortColumnId) {
        case 'fleetId':
          return (l1: { fleetId: string }, l2: { fleetId: string }) =>
            orderSign * l1.fleetId.localeCompare(l2.fleetId);
        default:
          return null;
      }
    };

    const comparator = getComparator(sortColumnId);

    if (comparator) {
      return [...fleets].sort(comparator);
    }

    return fleets;
  }, [fleets, sortParams]);

  const getAriaSortMessage = (columnId?: string, sortDirection?: SortDirection) => {
    const columnLabel = t(`fleetService.${columnId}`);
    const orderLabel =
      sortDirection === 'asc'
        ? t('global.paginatedTable.ascending')
        : t('global.paginatedTable.descending');
    return t('global.paginatedTable.sortedAriaMessage', { columnLabel, orderLabel });
  };

  return (
    <StandardListPage
      title={t('fleetService.pageTitle')}
      headerControl={<CreateFleetButton onReload={handleReload} />}
      data-testid="fleet-service-page"
      loading={loading}
      error={error}
      errorTitle={t('fleetService.loadError')}
      loadingTestId="fnds-fleet-service-init"
      errorTestId="fnds-fleet-service-no-data"
    >
      <PaginatedTable
        onSortChanged={(sortParams) => {
          setSortParams(sortParams);
          return true;
        }}
        getSortChangedAriaMessage={getAriaSortMessage}
        searchBoxProps={{ label: t('fleetService.searchLabel') }}
        sortedData={sortedFleets}
        search={search}
        noMatchesMessage={(searchExpression) => t('fleetService.noMatch', { searchExpression })}
        renderHead={() => (
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell
                id="fleet-table-header-fleetId"
                columnId="fleetId"
                tooltip={t('fleetService.sortByFleetId')}
              >
                {t('fleetService.fleetId')}
              </SortableTableHeaderCell>
              <TableCell id="fleet-table-header-region">{t('fleetService.region')}</TableCell>
              <TableCell id="fleet-table-header-stage">{t('fleetService.stage')}</TableCell>
              <TableCell id="fleet-table-header-accountId">{t('fleetService.accountId')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
        )}
        renderRow={(fleet, index) => (
          <TableRow
            key={fleet.name}
            aria-rowindex={index + 1}
            data-testid={`fleet-service-table-row-${fleet.name}`}
          >
            <TableCell aria-colindex={1} aria-describedby="fleet-table-header-fleet_id">
              {fleet.fleetId}
            </TableCell>
            <TableCell aria-colindex={1} aria-describedby="fleet-table-header-fleet_region">
              {fleet.region}
            </TableCell>
            <TableCell aria-colindex={1} aria-describedby="fleet-table-header-stage">
              {fleet.stage}
            </TableCell>
            <TableCell aria-colindex={1} aria-describedby="fleet-table-header-account_id">
              {fleet.accountId}
            </TableCell>
            <TableCell aria-colindex={6} tabIndex={-1} className={classes.enabledCell}>
              <LooseIconButton
                aria-label={t('tenantList.moreInformation')}
                component={Link}
                to={`/fleets/${fleet.id}/configuration`}
              >
                <Typography component="span" variant="inherit">
                  {t('global.view')}
                </Typography>
                <ChevronRight />
              </LooseIconButton>
            </TableCell>
          </TableRow>
        )}
      />
    </StandardListPage>
  );
};

export default Fleets;
